import moment from 'moment'
import {useCallback, useMemo} from 'react'

import {MINUTES_INTERVAL} from '../constants'
import {
  convertToLocaleTime,
  dateFormat,
  dateTimeFormat,
  timeFormat as defaultTimeFormat
} from '../timeUtils'
import {TimeSlot} from '../types'

export const useSlots = (
  dateTime: string | undefined,
  availableSlots: string[],
  earliestLoadTime: string,
  latestLoadTime: string,
  businessHoursStart: string,
  businessHoursEnd: string,
  isOutOfAutoConfirmLeadTime: boolean,
  timeZone: string | undefined,
  timeFormat: string = defaultTimeFormat
) => {
  const getLocalTime = useCallback(
    (timeSlot: string) => {
      if (!timeSlot) {
        return timeSlot
      }
      const dateTimeSlot = `${moment(dateTime).format(dateFormat)} ${timeSlot}`
      // When no plant timezone is configured we cannot convert to local time,
      // but we still return a full datetime string. A bare "HH:mm:ss" value does
      // not match dateTimeFormat and would produce an invalid moment downstream,
      // collapsing the whole slot grid to an empty list (blank Time step).
      if (!timeZone) {
        return dateTimeSlot
      }
      return convertToLocaleTime(dateTimeSlot, timeZone, dateTimeFormat)
    },
    [timeZone, dateTime]
  )

  const {
    localEarliestLoadTime,
    localLatestLoadTime,
    localBusinessHoursStart,
    localBusinessHoursEnd
  } = useMemo(
    () => ({
      localEarliestLoadTime: getLocalTime(earliestLoadTime),
      localLatestLoadTime: getLocalTime(latestLoadTime),
      localBusinessHoursStart: getLocalTime(businessHoursStart),
      localBusinessHoursEnd: getLocalTime(businessHoursEnd)
    }),
    [getLocalTime, earliestLoadTime, latestLoadTime, businessHoursStart, businessHoursEnd]
  )

  const localizedAvailableSlots = availableSlots.map((slot) =>
    moment(slot, timeFormat).format(timeFormat)
  )

  return useMemo(() => {
    const earliestTime = moment(localEarliestLoadTime, dateTimeFormat)
    const latestTime = moment(localLatestLoadTime, dateTimeFormat)
    const businessStart = moment(localBusinessHoursStart, dateTimeFormat)
    const businessEnd = moment(localBusinessHoursEnd, dateTimeFormat)

    const startTime = moment.min(businessStart, earliestTime)
    const endTime = moment.max(businessEnd, latestTime)

    const slots: TimeSlot[] = []
    const current = startTime.clone()

    while (current.isSameOrBefore(endTime)) {
      const timeString = current.format(timeFormat)
      const isOutsideLoadHours = current.isBefore(earliestTime) || current.isAfter(latestTime)
      const isAvailable =
        !isOutOfAutoConfirmLeadTime && localizedAvailableSlots.includes(timeString)

      slots.push({
        slot: timeString,
        isAvailable,
        isOutsideLoadHours: isOutsideLoadHours
      })

      current.add(MINUTES_INTERVAL, 'minutes')
    }

    return slots
  }, [
    localEarliestLoadTime,
    localLatestLoadTime,
    localBusinessHoursStart,
    localBusinessHoursEnd,
    timeFormat,
    isOutOfAutoConfirmLeadTime,
    localizedAvailableSlots
  ])
}
